/* D2C Allocation — single source of truth for taxonomy.
 * Mirrors §2 of D2C_Allocation_TRD.md. Every user-facing string the UI renders
 * must come from this file via the t() helper below. Renaming a label here
 * propagates everywhere.
 */

export const TAXONOMY = {
  module: { name: 'D2C Allocation', short: 'Allocation' },

  tiers: {
    platinum: {
      id: 'Platinum',
      label: 'Platinum client',
      icon: '⭐',
      requiresStartOfJourneyOtp: true,
    },
    standard: {
      id: 'Standard',
      label: 'Standard client',
      icon: '',
      requiresStartOfJourneyOtp: false,
    },
  },

  modes: {
    margin: {
      id: 'margin',
      label: 'Highest Prozo Margin',
      icon: '🏆',
      desc:
        'Auto-rank couriers within each service type by (Merchant Revenue − Prozo Cost) descending. Default on rate-card upload.',
      tag: 'Prozo-optimised',
    },
    cheapest: {
      id: 'cheapest',
      label: 'Lowest Merchant Cost',
      icon: '💸',
      desc:
        'When multiple courier partners exist for the same service type, allocation will be prioritized to the lowest-cost courier partner as per merchant pricing, followed by other cost-efficient partners.',
      tag: 'Merchant-optimised',
      requiresCpWiseRateCard: true,
    },
    custom: {
      id: 'custom',
      label: 'Custom rules',
      icon: '🎛',
      desc:
        'Lane-by-lane rules with multi-select service types. Multiple rules on the same lane merge into one Priority 1..N cascade in speed order.',
      tag: 'Flexible',
    },
  },

  pricingSources: {
    auto: {
      id: 'auto',
      label: 'Auto-pull from Pricing',
      icon: '🤖',
      desc:
        'Use the merchant pricing file already generated by the Merchant Pricing Module and ingested into Prozo admin. No upload needed.',
      hidesMerchantUpload: true,
    },
    upload: {
      id: 'upload',
      label: 'Upload existing pricing file',
      icon: '📂',
      desc:
        'Upload a merchant rate card CSV manually. Each cell becomes inline-editable before allocation runs.',
      hidesMerchantUpload: false,
    },
  },

  // Allocation-source picker. Two distinct groups depending on whether the merchant
  // already has an active allocation in Proship.
  // `backendStatus: 'gap'` items currently surface a feasibility banner — the
  // backend team needs to wire the GET endpoint (see _BACKEND_GAPS.md). They still
  // proceed in staging using DUMMY data so the rest of the flow can be exercised.
  allocationSources: {
    // ── Path A: existing allocation in Proship ────────────────────────────
    hydrate_ui: {
      id: 'hydrate_ui',
      label: 'Edit in UI (auto-pull from Proship)',
      icon: '🖥',
      desc:
        "Pull the merchant's current allocation from the Proship backend and hydrate it into this UI so you can edit interactively. Best for incremental tweaks.",
      group: 'existing',
      backendStatus: 'gap',
      gapNote:
        'No GET endpoint exists today to read out a merchant\'s current allocation in the rule shape this UI uses. Staging hydrates from local fixtures only — backend team needs to add GET /api/merchants/:id/allocation returning the normalized rule JSON.',
    },
    download_edit_upload: {
      id: 'download_edit_upload',
      label: 'Download CSV → edit → re-upload',
      icon: '📥',
      desc:
        "Generate the merchant's current 39-column allocation CSV. Edit it offline and upload the modified file. Best for bulk edits.",
      group: 'existing',
      backendStatus: 'gap',
      gapNote:
        'No GET endpoint exists today to export the current allocation as the 39-column CSV. Storage format is normalized JSON (mappedOrderRule.rules tree) while upload expects flat CSV — a JSON→CSV transformer is required. Staging downloads a CSV built from local fixtures.',
    },
    // ── Path B: no existing allocation (fresh setup) ──────────────────────
    scratch: {
      id: 'scratch',
      label: 'Start from scratch',
      icon: '✨',
      desc:
        'Begin with an empty allocation. Build rules step-by-step in the UI. Recommended for newly onboarded merchants.',
      group: 'new',
      backendStatus: 'ready',
    },
    upload_manual: {
      id: 'upload_manual',
      label: 'Upload manual CSV as starting point',
      icon: '📤',
      desc:
        'Start from a manually-prepared 39-column CSV. Pre-fills the rest of the flow so you can refine before publishing.',
      group: 'new',
      backendStatus: 'ready',
    },
  },

  zones: [
    { id: 'LOCAL',    label: 'Local' },
    { id: 'ROI',      label: 'ROI' },
    { id: 'REGIONAL', label: 'Regional' },
    { id: 'METROS',   label: 'Metros' },
    { id: 'SPECIAL',  label: 'Special' },
  ],

  weightBands: [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 40],

  paymentModes: [
    { id: 'PREPAID', label: 'Prepaid' },
    { id: 'COD',     label: 'COD' },
  ],

  qcModes: [
    { id: 'QC',   label: 'With QC' },
    { id: 'NOQC', label: 'Non-QC' },
  ],

  loadClasses: [
    { id: 'Regular', label: 'Regular', icon: '📦' },
    { id: 'Large',   label: 'Large',   icon: '🚛' },
  ],

  // 5 non-overlapping margin bands. UI shows the categorical pill only — never the raw %.
  marginBands: [
    { id: 'prof',  label: 'Profitable',             short: 'Profitable',    min: 15.0001, max: Infinity },
    { id: 'bprof', label: 'Borderline Profitable',  short: 'Borderline +',  min: 0,       max: 15 },
    { id: 'bloss', label: 'Borderline Loss-Making', short: 'Borderline −',  min: -4.9999, max: -0.0001 },
    { id: 'loss',  label: 'Loss-Making',            short: 'Loss',          min: -10,     max: -5 },
    { id: 'dloss', label: 'Deeply Loss-Making',     short: 'Deep Loss',     min: -Infinity, max: -10.0001 },
  ],

  // App-internal RBAC concepts (not Proship admin RBAC). Used to gate Platinum OTP email edit.
  roles: {
    user:        { id: 'user',        label: 'User',        canEditPlatinumOtpEmail: false },
    admin:       { id: 'admin',       label: 'Admin',       canEditPlatinumOtpEmail: true },
    super_admin: { id: 'super_admin', label: 'Super-admin', canEditPlatinumOtpEmail: true },
  },

  approval: {
    otpDigits: 6,
    otpValidityMinutes: 10,
    otpSubjectTemplate: '[Prozo Platinum] OTP for {{merchantName}} allocation edit — {{otp}}',
    otpSenderEmail: 'no-reply@prozo.com',
    emailApprovalRecipients: ['compliance@prozo.com', 'pricing-team@prozo.com'],
    lossMakingTriggerBands: ['loss', 'dloss'],
  },

  csvSchema: {
    fixedColumns: [
      'Delivery Type', 'Zone', 'Min Weight (kg)', 'Max Weight (kg)',
      'Min Invoice Value(INR)', 'Max Invoice Value(INR)',
      'Pickup Pincode', 'Drop Pincode', 'Payment Mode',
      'Dispatch Mode', 'Shipment Type', 'RVP Reason',
      'Item Units', 'Unique SKU Count', 'Order Creation Time(IST)',
      'QC Type', 'ODA Value(s)', 'ABD Flag',
    ],
    priorityColumns: 20,
    trailingColumns: ['Channel Name'],
  },
};

export const HELP_TEXTS = {
  platinum_client: {
    title: 'Platinum client',
    body:
      'Platinum merchants are high-value clients whose allocation cannot be edited without OTP authorisation. The OTP is sent to the registered admin email on this card. Both at the start of an edit journey and on Publish.',
    example: 'MileCollective is Platinum. Any rule change → OTP → Publish.',
  },
  platinum_otp_email: {
    title: 'Platinum OTP email',
    body:
      'All platinum-client OTPs are sent to the email registered here. Only Admin and Super-admin can edit this address. Other users see it read-only.',
    example: 'platinum-approvals@prozo.com',
  },
  pickup_pincodes: {
    title: 'Merchant pickup pincodes',
    body:
      "Origin pincodes from which this merchant ships. Used to validate the FWD Pickup Pincode condition on rules + the RVP Drop Pincode condition on reverse rules. A pincode used in a rule that is NOT in this list will prompt a confirmation on save (warn, not block).",
    example: 'MileCollective: 110037, 400072, 560001.',
  },
  rate_card_source: {
    title: 'Pricing source',
    body:
      'Auto-pull uses the merchant pricing file already generated by the Pricing Wizard. Upload lets you bring your own CSV which becomes inline-editable after upload.',
    example: '',
  },
  service_types: {
    title: 'Service types',
    body:
      'Speed-ranked categories. Faster types cascade first (SDD → SC-NDD → IC-NDD → Air → Surface). Couriers within a type are ranked by margin or cost depending on rule priority.',
    example: '',
  },
  weight_ceiling: {
    title: 'Weight ceiling',
    body: "Maximum chargeable shipment weight (in kg) the merchant's contract supports. Lanes beyond this are non-serviceable.",
    example: '10 kg',
  },
  invoice_ceiling: {
    title: 'Invoice ceiling',
    body: 'Maximum invoice value the merchant can ship under this contract.',
    example: '₹50,000',
  },
  hv_threshold: {
    title: 'High-Value threshold',
    body: 'Invoice value at and above which the High-Value service-type cascade activates (prepended in cascade order).',
    example: '≥ ₹10,000',
  },
  arrangement_type: {
    title: 'Arrangement type',
    body: 'Zone × Weight slab combination from the Pricing Wizard: ZxW, ZWZ, ZAWS, ZAWA or WAZ.',
    example: 'ZWZ — Zone × Weight slab',
  },
  rate_card_type: {
    title: 'Rate-card type',
    body:
      'Blended = single rate per (zone, weight, payment) for all parents. CP-wise = per-parent rates (required for Lowest Merchant Cost mode).',
    example: '',
  },
  supply_override: {
    title: 'Supply-side override',
    body: 'Read-only baseline. Use the download template to send corrections, then re-upload here to override.',
    example: '',
  },
  mode_margin: {
    title: 'Highest Prozo Margin',
    body:
      'Auto-applied to every lane on rate-card upload. Couriers are cascaded by service type, and within each service type ranked by margin = (Merchant Revenue − Prozo Cost) descending.',
    example: '',
  },
  mode_cheapest: {
    title: 'Lowest Merchant Cost',
    body:
      'Allocates to the lowest-cost courier partner per service type. Requires a CP-wise (per-courier) rate card.',
    example: '',
  },
  mode_custom: {
    title: 'Custom rules',
    body:
      'Lane-by-lane rules. Multiple rules on the same lane merge into one Priority 1..N cascade in speed order.',
    example: '',
  },
  load_class: {
    title: 'Load class',
    body: 'Regular (≤10 kg) or Large (10–40 kg). Mutually exclusive per rule.',
    example: '',
  },
  priority_type: {
    title: 'Priority type',
    body: 'How couriers inside this rule are ranked: by margin desc, by cost asc, or by an explicit custom order you set.',
    example: '',
  },
  granularity: {
    title: 'Granularity',
    body: 'Show couriers at parent level (e.g. Delhivery) or at account-code level (e.g. Delhivery_ENT_SURFACE).',
    example: '',
  },
  qc_mode: {
    title: 'QC mode',
    body: 'Reverse-only condition. With QC = quality check at pickup; Non-QC = no check.',
    example: '',
  },
  coverage_analytics: {
    title: 'Coverage analytics',
    body: 'Percentage of lanes (zone × weight × payment) covered by main rules vs fallback vs uncovered.',
    example: '',
  },
  serviceability_summary: {
    title: 'Serviceability summary',
    body: 'Coverage and profit breakdown across FWD Prepaid, FWD COD, RVP without QC and RVP with QC.',
    example: '',
  },
  margin_bands: {
    title: 'Margin classification (5 bands)',
    body:
      'Profitable (>15%) · Borderline Profitable (0–15%) · Borderline Loss (−5–0) · Loss-Making (−10–−5) · Deeply Loss-Making (<−10). Categories show on UI; the raw % is hidden by design.',
    example: 'Borderline Profitable means positive but below the +15% threshold.',
  },
  otp_autofill_demo: {
    title: 'Auto-fill (demo)',
    body: 'Staging convenience: generates a random 6-digit code. In production an email arrives at the registered platinum OTP address.',
    example: '',
  },
};

// Walk a dotted path through TAXONOMY. Missing keys render as `??path??` so they
// stand out during code review.
export function t(path) {
  if (!path) return '';
  const parts = path.split('.');
  let cur = TAXONOMY;
  for (const p of parts) {
    if (cur == null) return `??${path}??`;
    cur = cur[p];
  }
  return cur == null ? `??${path}??` : cur;
}

export const ZONE_BY_ID = Object.fromEntries(TAXONOMY.zones.map((z) => [z.id, z]));

// Display labels for known service type IDs.
const ST_LABEL_MAP = {
  SUB2H: 'Sub-2hr',
  H2TO4: '2–4hr',
  SDD_FWD_REG: 'SDD (Same-Day)',
  SC_NDD_FWD_REG: 'Same-City NDD',
  INTERCITY_NDD_REG: 'Inter-City NDD',
  FWD_AIR_REG: 'Air (Regular)',
  FWD_SURFACE_REG: 'Surface (Regular)',
  SDD_FWD_LARGE: 'SDD (Large)',
  SC_NDD_FWD_LARGE: 'Same-City NDD (Large)',
  INTERCITY_NDD_LARGE: 'Inter-City NDD (Large)',
  FWD_AIR_LARGE: 'Air (Large)',
  FWD_SURFACE_LARGE: 'Surface (Large)',
  HV_FWD_REG: 'High-Value Forward (Regular)',
  HV_FWD_LARGE: 'High-Value Forward (Large)',
  REV_REG_QC: 'Reverse (Regular, with QC)',
  REV_REG_NOQC: 'Reverse (Regular, Non-QC)',
  REV_LARGE_QC: 'Reverse (Large, with QC)',
  REV_LARGE_NOQC: 'Reverse (Large, Non-QC)',
  HV_REVERSE_REG: 'High-Value Reverse (Regular)',
  HV_REVERSE_LARGE: 'High-Value Reverse (Large)',
};

// Cascade rank (lower = faster). Used for ordering chip rails and CSV rows.
const ST_RANK_MAP = {
  SUB2H: 1, H2TO4: 2,
  SDD_FWD_REG: 3, SDD_FWD_LARGE: 3,
  SC_NDD_FWD_REG: 4, SC_NDD_FWD_LARGE: 4,
  INTERCITY_NDD_REG: 5, INTERCITY_NDD_LARGE: 5,
  FWD_AIR_REG: 6, FWD_AIR_LARGE: 6,
  FWD_SURFACE_REG: 7, FWD_SURFACE_LARGE: 7,
  HV_FWD_REG: 0, HV_FWD_LARGE: 0,
  REV_REG_QC: 1, REV_REG_NOQC: 2,
  REV_LARGE_QC: 1, REV_LARGE_NOQC: 2,
  HV_REVERSE_REG: 0, HV_REVERSE_LARGE: 0,
};

// Build a full descriptor for a service type ID.
// Structural fields (dir / load / hv / qc) are inferred from naming conventions
// so no hardcoded catalog array is needed.
export function stDescFromId(id) {
  if (!id) return null;
  const u = id.toUpperCase();
  const hv   = u.startsWith('HV_');
  const isRev = (u.startsWith('REV_') || u.startsWith('HV_REVERSE'));
  const dir  = isRev ? 'Reverse' : 'Forward';
  const load = u.endsWith('_LARGE') ? 'Large' : 'Regular';
  const qc   = isRev ? (u.endsWith('_NOQC') ? 'NOQC' : 'QC') : undefined;
  return {
    id,
    label: ST_LABEL_MAP[id] || id,
    rank:  ST_RANK_MAP[id] ?? 99,
    dir,
    load,
    hv,
    ...(qc !== undefined ? { qc } : {}),
  };
}

// Derive service type descriptors from the serviceability groups API response.
// Each group object has: { code, name, category, direction, weightBand, linkedCodes[] }
// where `code` is the ST ID (e.g. 'FWD_SURFACE_REG') and `name` is the display label.
export function deriveServiceTypesFromGroups(groups = []) {
  return (groups || [])
    .filter((g) => g.code)
    .map((g) => {
      const u = (g.code || '').toUpperCase();
      const isReverse = g.direction === 'RVP' || g.category === 'REVERSE'
        || u.startsWith('REV_') || u.startsWith('HV_REVERSE');
      const isHV = g.category === 'HIGH_VALUE' || u.startsWith('HV_');
      const isLarge = g.weightBand === 'LARGE' || u.endsWith('_LARGE');
      const qcVal = isReverse ? (u.endsWith('_NOQC') ? 'NOQC' : 'QC') : undefined;
      return {
        id: g.code,
        label: g.name || ST_LABEL_MAP[g.code] || g.code,
        rank: ST_RANK_MAP[g.code] ?? 99,
        dir: isReverse ? 'Reverse' : 'Forward',
        load: isLarge ? 'Large' : 'Regular',
        hv: isHV,
        ...(qcVal !== undefined ? { qc: qcVal } : {}),
      };
    })
    .sort((a, b) => {
      if (a.dir !== b.dir) return a.dir === 'Forward' ? -1 : 1;
      return a.rank - b.rank;
    });
}

// Classify a margin % into one of the 5 bands. UI consumers should use bandShortLabel(band)
// and never expose the underlying %.
export function classifyMargin(pct) {
  if (pct == null || Number.isNaN(pct)) return null;
  for (const band of TAXONOMY.marginBands) {
    if (pct >= band.min && pct <= band.max) return band;
  }
  return null;
}

// Map a band id to a MUI palette role + tone. The actual hex comes from the MUI theme,
// not from raw colors in TAXONOMY — this keeps the implementation on-brand.
export const MARGIN_BAND_THEME = {
  prof:  { paletteRole: 'success', tone: 'main' },
  bprof: { paletteRole: 'success', tone: 'light' },
  bloss: { paletteRole: 'warning', tone: 'main' },
  loss:  { paletteRole: 'error',   tone: 'main' },
  dloss: { paletteRole: 'error',   tone: 'dark' },
  unk:   { paletteRole: 'grey',    tone: 'main' },
};

export const TABS = [
  { id: 'listing',  label: 'Allocations',         required: true,  utility: false },
  { id: 'merchant', label: 'Merchant Details',    required: true,  utility: false, step: 1 },
  { id: 'rates',    label: 'Rate Cards',          required: true,  utility: false, step: 2 },
  { id: 'accounts', label: 'All Account Codes',   required: false, utility: false, step: 3 },
  { id: 'forward',  label: 'Forward Allocation',  required: true,  utility: false, step: 4 },
  { id: 'reverse',  label: 'Reverse Pickup',      required: false, utility: false, step: 5 },
  { id: 'preview',  label: 'Preview & Publish',   required: true,  utility: false, step: 6 },
  { id: 'log',      label: 'Change Log',          required: false, utility: true },
];

// RBAC role allowed to use this module. Wired in PrivateRoute via <ProtectedRoute role='INTERNAL'>.
export const MODULE_RBAC = 'INTERNAL';
